/**
 * BANCO — Learning Bank.
 *
 * El repositorio del material REAL: errores propios, expresiones que hicieron
 * falta, situaciones del trabajo, problemas de listening y feedback recibido.
 * No es una lista genérica de vocabulario.
 *
 * Por defecto muestra solo lo VIVO —activo y aprendiendo—. Lo dominado y lo
 * archivado tiene su propia vista: el banco principal no puede llenarse de
 * cosas que ya se saben, o deja de ser útil.
 */

import { useLiveQuery } from 'dexie-react-hooks';
import { useState } from 'react';

import { AREAS, NOMBRE_AREA } from '../../domain/modelo/areas';
import {
  ESTADOS_ELEMENTO,
  KINDS,
  NOMBRE_KIND,
  type ElementoBanco,
  type EstadoElemento,
  type Kind,
} from '../../domain/modelo/banco';
import { NOMBRE_PRIORIDAD, PRIORIDADES } from '../../domain/modelo/prioridad';
import { bancoRepo, filtrarBanco, type FiltrosBanco } from '../../persistencia/bancoRepo';
import { Pantalla } from '../comunes/Pantalla';
import { Suave } from '../comunes/Tarjeta';
import { EditorElemento } from './EditorElemento';
import estilos from './Banco.module.css';

const NOMBRE_ESTADO: Record<EstadoElemento, string> = {
  activo: 'Activo',
  aprendiendo: 'Aprendiendo',
  dominado: 'Dominado',
  archivado: 'Archivado',
};

const NOMBRE_FUENTE: Record<string, string> = {
  baseline: 'Baseline',
  chatgpt: 'ChatGPT',
  trabajo: 'Trabajo',
  conversacion: 'Conversación',
  listening: 'Listening',
  manual: 'Manual',
};

const VIVOS: EstadoElemento[] = ['activo', 'aprendiendo'];
const HISTORIAL: EstadoElemento[] = ['dominado', 'archivado'];

function fecha(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-CR', { day: 'numeric', month: 'short' });
}

export function Banco() {
  const todos = useLiveQuery(() => bancoRepo.listar(), [], undefined);
  const [vista, setVista] = useState<'activos' | 'historial'>('activos');
  const [filtros, setFiltros] = useState<FiltrosBanco>({});
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [editando, setEditando] = useState<ElementoBanco | null>(null);
  const [confirmandoBorrado, setConfirmandoBorrado] = useState<string | null>(null);

  if (!todos) {
    return (
      <Pantalla titulo="Learning Bank">
        <Suave>Cargando…</Suave>
      </Pantalla>
    );
  }

  const deLaVista = todos.filter((e) =>
    (vista === 'activos' ? VIVOS : HISTORIAL).includes(e.estado),
  );
  const elementos = filtrarBanco(deLaVista, filtros);
  const enHistorial = todos.filter((e) => HISTORIAL.includes(e.estado)).length;

  function alternar<T>(lista: T[] | undefined, valor: T): T[] {
    const actual = lista ?? [];
    return actual.includes(valor)
      ? actual.filter((v) => v !== valor)
      : [...actual, valor];
  }

  return (
    <Pantalla titulo="Learning Bank" tituloVisible>
      <div className={estilos.pestanas} role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={vista === 'activos'}
          className={vista === 'activos' ? `${estilos.pestana} ${estilos.pestanaActiva}` : estilos.pestana}
          onClick={() => setVista('activos')}
        >
          En uso
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={vista === 'historial'}
          className={vista === 'historial' ? `${estilos.pestana} ${estilos.pestanaActiva}` : estilos.pestana}
          onClick={() => setVista('historial')}
        >
          Historial ({enHistorial})
        </button>
      </div>

      <div className={estilos.buscador}>
        <input
          type="search"
          className={estilos.busqueda}
          placeholder="Buscar en tus elementos"
          aria-label="Buscar en el Learning Bank"
          value={filtros.texto ?? ''}
          onChange={(e) => setFiltros((f) => ({ ...f, texto: e.target.value }))}
        />
        <button
          type="button"
          className={estilos.botonFiltros}
          onClick={() => setMostrarFiltros((v) => !v)}
          aria-expanded={mostrarFiltros}
        >
          Filtros
        </button>
      </div>

      {mostrarFiltros ? (
        <div className={estilos.filtros}>
          <GrupoFiltro titulo="Estado">
            {(vista === 'activos' ? VIVOS : HISTORIAL).map((estado) => (
              <Chip
                key={estado}
                activo={filtros.estados?.includes(estado) ?? false}
                onClick={() =>
                  setFiltros((f) => ({ ...f, estados: alternar(f.estados, estado) }))
                }
              >
                {NOMBRE_ESTADO[estado]}
              </Chip>
            ))}
          </GrupoFiltro>

          <GrupoFiltro titulo="Tipo">
            {KINDS.map((kind) => (
              <Chip
                key={kind}
                activo={filtros.kinds?.includes(kind) ?? false}
                onClick={() => setFiltros((f) => ({ ...f, kinds: alternar(f.kinds, kind) }))}
              >
                {NOMBRE_KIND[kind]}
              </Chip>
            ))}
          </GrupoFiltro>

          <GrupoFiltro titulo="Área">
            <Chip
              activo={filtros.areas?.includes('sin_area') ?? false}
              onClick={() =>
                setFiltros((f) => ({ ...f, areas: alternar(f.areas, 'sin_area' as const) }))
              }
            >
              Sin clasificar
            </Chip>
            {AREAS.map((area) => (
              <Chip
                key={area}
                activo={filtros.areas?.includes(area) ?? false}
                onClick={() => setFiltros((f) => ({ ...f, areas: alternar(f.areas, area) }))}
              >
                {NOMBRE_AREA[area]}
              </Chip>
            ))}
          </GrupoFiltro>

          <GrupoFiltro titulo="Prioridad">
            {PRIORIDADES.map((p) => (
              <Chip
                key={p}
                activo={filtros.prioridades?.includes(p) ?? false}
                onClick={() =>
                  setFiltros((f) => ({ ...f, prioridades: alternar(f.prioridades, p) }))
                }
              >
                {NOMBRE_PRIORIDAD[p]}
              </Chip>
            ))}
          </GrupoFiltro>

          {Object.keys(filtros).length > 0 ? (
            <button
              type="button"
              className={estilos.limpiar}
              onClick={() => setFiltros({})}
            >
              Quitar filtros
            </button>
          ) : null}
        </div>
      ) : null}

      {elementos.length === 0 ? (
        <div className={estilos.vacio}>
          {todos.length === 0 ? (
            <>
              <p>Todavía está vacío.</p>
              <Suave>
                Se llena con material real: tus errores, expresiones que te hicieron
                falta, situaciones del trabajo, problemas de listening y el feedback de
                ChatGPT. Los primeros salen del baseline; después, de la captura rápida
                desde Hoy.
              </Suave>
            </>
          ) : (
            <p>Nada coincide con lo que buscás.</p>
          )}
        </div>
      ) : (
        <ul className={estilos.lista}>
          {elementos.map((elemento) => (
            <li key={elemento.id} className={estilos.elemento}>
              <div className={estilos.etiquetas}>
                <span className={estilos.kind}>{NOMBRE_KIND[elemento.kind]}</span>
                <span className={estilos.area}>
                  {elemento.area ? NOMBRE_AREA[elemento.area] : 'Sin clasificar'}
                </span>
                <span className={estilos.prioridad} data-prioridad={elemento.prioridad}>
                  {NOMBRE_PRIORIDAD[elemento.prioridad]}
                </span>
                <span className={estilos.estado}>{NOMBRE_ESTADO[elemento.estado]}</span>
              </div>

              <p className={estilos.contexto}>{elemento.contextoOriginal}</p>
              {elemento.correccionTarget.trim() ? (
                <p className={estilos.target}>{elemento.correccionTarget}</p>
              ) : null}
              {elemento.notas?.trim() ? (
                <p className={estilos.notas}>{elemento.notas}</p>
              ) : null}

              <p className={estilos.meta}>
                {NOMBRE_FUENTE[elemento.fuente] ?? elemento.fuente}
                {elemento.vecesOcurrido > 1 ? ` · volvió a pasar ×${elemento.vecesOcurrido}` : ''}
                {elemento.ultimaPracticaEn
                  ? ` · practicado ${fecha(elemento.ultimaPracticaEn)}`
                  : ' · sin practicar'}
              </p>

              <div className={estilos.acciones}>
                <button
                  type="button"
                  className={estilos.accion}
                  onClick={() => setEditando(elemento)}
                >
                  Editar
                </button>

                {VIVOS.includes(elemento.estado) ? (
                  <>
                    <button
                      type="button"
                      className={estilos.accion}
                      onClick={() => void bancoRepo.registrarOcurrencia(elemento.id)}
                    >
                      Volvió a pasar
                    </button>
                    {elemento.estado === 'activo' ? (
                      <button
                        type="button"
                        className={estilos.accion}
                        onClick={() => void bancoRepo.cambiarEstado(elemento.id, 'aprendiendo')}
                      >
                        Marcar aprendiendo
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className={estilos.accion}
                      onClick={() => void bancoRepo.cambiarEstado(elemento.id, 'dominado')}
                    >
                      Ya lo domino
                    </button>
                    <button
                      type="button"
                      className={estilos.accionSuave}
                      onClick={() => void bancoRepo.cambiarEstado(elemento.id, 'archivado')}
                    >
                      Archivar
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className={estilos.accion}
                    onClick={() => void bancoRepo.cambiarEstado(elemento.id, 'activo')}
                  >
                    Reactivar
                  </button>
                )}

                <button
                  type="button"
                  className={estilos.accionSuave}
                  onClick={() => setConfirmandoBorrado(elemento.id)}
                >
                  Eliminar
                </button>
              </div>

              {confirmandoBorrado === elemento.id ? (
                <div className={estilos.confirmacion} role="dialog" aria-label="Eliminar elemento">
                  <p>¿Eliminarlo? Si solo querés sacarlo de en medio, mejor archivalo.</p>
                  <div className={estilos.acciones}>
                    <button
                      type="button"
                      className={estilos.accion}
                      onClick={() => setConfirmandoBorrado(null)}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className={estilos.peligro}
                      onClick={() => {
                        void bancoRepo.borrar(elemento.id);
                        setConfirmandoBorrado(null);
                      }}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {vista === 'historial' && enHistorial > 0 ? (
        <Suave>
          Lo dominado y lo archivado no se borra nunca y no entra en el Día 4. Podés
          reactivar cualquier cosa cuando quieras.
        </Suave>
      ) : null}

      {editando ? (
        <EditorElemento
          elemento={editando}
          onCerrar={() => setEditando(null)}
        />
      ) : null}
    </Pantalla>
  );
}

function GrupoFiltro({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div className={estilos.grupoFiltro}>
      <span className={estilos.tituloFiltro}>{titulo}</span>
      <div className={estilos.chips}>{children}</div>
    </div>
  );
}

function Chip({
  activo,
  onClick,
  children,
}: {
  activo: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={activo ? `${estilos.chip} ${estilos.chipActivo}` : estilos.chip}
      aria-pressed={activo}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export { ESTADOS_ELEMENTO, NOMBRE_ESTADO };
export type { Kind };
